import { MenuAction } from '@anecdotes/design-system/dropdown-menu';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnInit } from '@angular/core';
import { firstValueFrom, map, Observable, shareReplay } from 'rxjs';
import {
  CompanyResponse,
  ProfileResponse,
  TrustCenterFacadeService,
  TrustCenterSharedModalsService,
} from '@anecdotes/shared-trust-center';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

export enum ProfileActionState {
  default = 'default',
  edit = 'edit',
  delete = 'delete',
}

@Component({
  selector: 'app-profile-item',
  templateUrl: './profile-item.component.html',
  styleUrls: ['./profile-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProfileItemComponent implements OnInit {
  readonly companiesMapping: { [key: string]: string } = {
    '=0': this.buildTranslationKey('noCompanies'),
    '=1': this.buildTranslationKey('companiesCount.singular'),
    other: this.buildTranslationKey('companiesCount.plural'),
  };

  private canBeDeleted: boolean;

  readonly profileActionStateEnum = ProfileActionState;

  readonly menuActions: MenuAction[] = [
    {
      id: 'editProfile',
      translationKey: this.buildTranslationKey('edit'),
      action: () => this.editProfileAction(),
    },
    {
      id: 'deleteProfile',
      translationKey: this.buildTranslationKey('delete'),
      tooltipByCondition: () => {
        if (!this.canBeDeleted) {
          return this.translate.instant(this.buildTranslationKey('disabledDeleteTooltip'));
        }
      },
      disabledCondition: () => !this.canBeDeleted,
      action: () => this.deleteProfileAction(),
    },
  ];

  @Input()
  profileId: string;

  profile$: Observable<ProfileResponse>;
  companiesCounts$: Observable<number>;
  isDefaultProfile$: Observable<boolean>;
  profileActionState: ProfileActionState = ProfileActionState.default;

  constructor(
    private readonly trustCenterFacadeService: TrustCenterFacadeService,
    private readonly trustCenterModalsService: TrustCenterSharedModalsService,
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.profile$ = this.trustCenterFacadeService.getProfileById(this.profileId);

    this.companiesCounts$ = this.trustCenterFacadeService.getCompanies().pipe(
      map(
        (comapnies: CompanyResponse[]) =>
          comapnies?.filter((company: CompanyResponse) => company.permission_profile_id === this.profileId)?.length,
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.isDefaultProfile$ = this.trustCenterFacadeService.getDefaultProfile().pipe(
      map((defaultProfile: ProfileResponse) => defaultProfile?.id === this.profileId),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.trustCenterFacadeService.canProfileBeDeleted(this.profileId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((canBeDeleted: boolean) => {
        this.canBeDeleted = canBeDeleted;
        this.cd.detectChanges();
      });
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.permissionProfilesSettings.${key}`;
  }

  resetProfileState(): void {
    this.updateProfileState(ProfileActionState.default);
  }

  openAddEditProfileModal(profileId?: string): void {
    this.trustCenterModalsService.openAddEditProfileModal(profileId);
    this.resetProfileState();
  }

  async deleteProfile(): Promise<void> {
    const profile = await firstValueFrom(this.profile$);
    await this.trustCenterFacadeService.deleteProfile(profile);

    this.resetProfileState();
  }

  private updateProfileState(profileState: ProfileActionState): void {
    this.profileActionState = profileState;
    this.cd.detectChanges();
  }

  private async editProfileAction(): Promise<void> {
    if ((await firstValueFrom(this.companiesCounts$)) > 0) {
      this.updateProfileState(ProfileActionState.edit);
    } else {
      this.openAddEditProfileModal(this.profileId);
    }
  }

  private async deleteProfileAction(): Promise<void> {
    if ((await firstValueFrom(this.companiesCounts$)) > 0) {
      this.updateProfileState(ProfileActionState.delete);
    } else {
      this.deleteProfile();
    }
  }
}
